import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { nullValidator } from '../../../propTypes/validators';
import { theme } from '../../../../domain/theme';
import { facePaint } from '../../../../domain/theme/utils/facePaint';
import { reverseCssUnits } from '../../../../domain/theme/utils/reverseCssUnits';

// We use padding to get the gutter, but it can also be achieved
// using "border: 1rem solid transparent;".
// "background-clip: padding-box;" can be used if we need to add a background
// that extends to the inside edge of the border.
// @see https://developer.mozilla.org/en-US/docs/Web/CSS/background-clip
export const GridRow = ({ wrap, direction, justify, gutter, children }) => {
  if (process.env.NODE_ENV === 'development') {
    if (children) {
      const childrenArray = Array.isArray(children) ? children : [children];
      childrenArray.forEach(child => {
        if (
          child !== null &&
          child.type?.name !== 'GridColumn' &&
          child.type?.name !== 'GridColumnGroup' &&
          child.type?.name !== 'LeftContentPackage' &&
          child.type?.name !== 'RightContentPackage'
        ) {
          throw Error(
            `Prop 'children' in 'GridRow' must be a group of 'GridColumn' components.`
          );
        }
      });
    } else {
      throw Error(`Prop 'children' can not be empty in 'GridRow'`);
    }
  }

  const styles = {
    /* Required to hide the negative margin from row */
    container: css`
      contain: paint;
    `,

    row: css`
      display: flex;
      /* Default value */
      margin: -${theme.grid.gutter('base')};
      ${facePaint({
        margin: reverseCssUnits(theme.grid.gutter.parseUnknownData(gutter)),
        'flex-wrap': wrap,
        'flex-direction': direction,
        'justify-content': justify
      })};

      & > div {
        /* Default value */
        padding: ${theme.grid.gutter('base')};
        ${facePaint({
          padding: theme.grid.gutter.parseUnknownData(gutter)
        })};
      }
    `
  };

  // Keep "grid-row" class to be able to disable "contain: paint" when
  // something inside a GridRow needs to expand over its boundaries,
  // i.e.- ContentTakeOver, a popup, etc
  return (
    <div css={styles.container} className="grid-row">
      <div css={styles.row}>{children}</div>
    </div>
  );
};

GridRow.defaultProps = {
  wrap: 'wrap',
  direction: 'row',
  justify: 'flex-start',
  gutter: 'base'
};

GridRow.propTypes = {
  // flex-wrap
  wrap: PropTypes.oneOfType([
    PropTypes.oneOf(['wrap', 'wrap-reverse']),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        nullValidator,
        PropTypes.oneOf(['wrap', 'wrap-reverse'])
      ])
    )
  ]),
  // flex-direction
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(['row', 'row-reverse']),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        nullValidator,
        PropTypes.oneOf(['row', 'row-reverse'])
      ])
    )
  ]),
  // justify-content
  justify: PropTypes.oneOfType([
    PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        nullValidator,
        PropTypes.oneOf(['flex-start', 'center', 'flex-end'])
      ])
    )
  ]),
  gutter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([nullValidator, PropTypes.string]))
  ]),
  children: PropTypes.node.isRequired
};
